import { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin,
  Textarea
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon } from 'tdesign-icons-react';
import { dictApi } from '../../api';
import type { DictItem, DictItemDTO } from '../../types';
import CoverImageUploader from '../../components/CoverImageUploader';
const typeOptions = [
  { label: 'JSON', value: 'JSON' },
  { label: 'DECIMAL', value: 'DECIMAL' },
];
export default function DictManage() {
  const [data, setData] = useState<DictItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<DictItem | null>(null);
  const [searchForm] = Form.useForm();
  const [currentValueType, setCurrentValueType] = useState<string>('DECIMAL');
  const [filterGroup, setFilterGroup] = useState('');

  const formRef = useRef<any>(null);

  const columns = [
    { colKey: 'name', title: '名称', width: 150, ellipsis: true },
    { colKey: 'dictGroup', title: '分组', width: 150 },
    {
      colKey: 'dictKey',
      title: '字典Key',
      width: 150,
      cell: ({ row }: any) => (
        <span 
          className="text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
        >
          {row.dictKey}
        </span>
      ),
    },
    { colKey: 'dictLabel', title: '字典标签', width: 150 },
    { colKey: 'dictValue', title: '字典值', width: 150 },
    // {
    //   colKey: 'editable',
    //   title: '状态',
    //   width: 100,
    //   cell: ({ row }: any) => (
    //     <Switch value={row.editable === 1} onChange={(val) => handleStatusChange(row, val)} />
    //   ),
    // },
    { colKey: 'sort', title: '排序', width: 80 },
    { colKey: 'updateTime', title: '更新时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      cell: ({ row }: any) => (
        <Space>
          <Button theme="success" variant="text" icon={<EditIcon />} onClick={() => handleEdit(row)}>编辑</Button>
          <Popconfirm content="确定删除该字典项？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (params: { pageNum?: number; pageSize?: number; dictGroup?: string; dictKey?: string } = {}) => {
    setLoading(true);
    try {
        const rawSearchParams  = searchForm.getFieldsValue?.(true) || {};
      // 清洗参数：移除 undefined, null 和空字符串
      const searchParams = Object.keys(rawSearchParams).reduce((acc, key) => {
        const value = rawSearchParams[key];
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      const res = await dictApi.getPageList({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
        ...params,
      });
      if (res.code === 200) {
        setData(res.data.records || res.data.list || []);
        setPagination({
          current: res.data.pageNum || res.data.current || 1,
          pageSize: res.data.pageSize || res.data.size || 10,
          total: res.data.total || 0,
        });
      } else {
        MessagePlugin.error(res.message || '获取数据失败');
      }
    } catch {
      MessagePlugin.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (row: DictItem, val: boolean) => {
    try {
      const res = await dictApi.updateByKey(row.dictKey, { status: val ? 1 : 0 });
      if (res.code === 200) {
        MessagePlugin.success('状态更新成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '状态更新失败');
      }
    } catch {
      MessagePlugin.error('状态更新失败');
    }
  };

  const handleEdit = (item: DictItem) => {
    setEditingItem(item);
    const valueType = item.valueType || 'DECIMAL';
    setCurrentValueType(valueType);
    let dictValue = item.dictValue || '';
    if (valueType === 'JSON') {
      try {
        const parsed = JSON.parse(dictValue);
        if (parsed && typeof parsed === 'object') {
            setEditingItem({
                ...item,
                dictImage: parsed.imageUrl || '',
            });
        }
      } catch {
      }
    }
    setDialogVisible(true);
  };
  const resetFormState = () => {
    setEditingItem(null);
    setCurrentValueType('DECIMAL');
    if (formRef.current) {
      formRef.current.reset();
    }
  };
  const handleAdd = () => {
    resetFormState();
    setDialogVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await dictApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = await formRef.current?.validate();
      console.log(formData,'formData');
      if (!formData) return;
      const values = formRef.current?.getFieldsValue(true) || {};
      let dictValue = values.dictValue;
      if (values.valueType === 'JSON') {
        try {
          const parsed = JSON.parse(dictValue);
          if (values.dictImage) {
            parsed.imageUrl = values.dictImage;
          }
          dictValue = JSON.stringify(parsed);
        } catch {
          if (values.dictImage) {
            dictValue = JSON.stringify({ ...dictValue, imageUrl: values.dictImage });
          }
        }
      }

      const data: DictItemDTO = {
        ...values,
        dictValue: dictValue
      };
      const res = editingItem
        ? await dictApi.updateByKey(editingItem.dictKey, data)
        : await dictApi.create(data);

        if (res.code === 200) {
            MessagePlugin.success(editingItem ? '更新成功' : '创建成功');
            setDialogVisible(false);
            resetFormState();
            fetchData();
        } else {
            MessagePlugin.error(res.message || '操作失败');
        }
    } catch {
      MessagePlugin.error('操作失败');
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };

  const handleReset = () => {
    searchForm.reset();
    setPagination({ ...pagination, current: 1 });
    fetchData({ pageNum: 1 });
  };

  const handleFilterChange = () => {
    fetchData({ pageNum: 1 });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">数据字典管理</h2>
        <Button icon={<AddIcon />} onClick={handleAdd}>新增字典项</Button>
      </div>
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
            <Form form={searchForm} layout="inline">
                <Form.FormItem name="keyword" label="关键字查询">
                    <Input placeholder="请输入查询关键词" />
                </Form.FormItem>
                <Form.FormItem>
                    <Button theme="primary" onClick={handleSearch}>查询</Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>重置</Button>
                </Form.FormItem>
            </Form>
        </div>

      <Table
        data={data}
        columns={columns}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: ({ current, pageSize }) => {
            fetchData({ pageNum: current, pageSize });
          },
        }}
        rowKey="id"
      />

      <Dialog
        visible={dialogVisible}
        header={editingItem ? '编辑字典项' : '新增字典项'}
        onClose={() => {
            resetFormState();
            setDialogVisible(false)
        }}
        onConfirm={handleSubmit}
        confirmBtn="确定"
        cancelBtn="取消"
        width={600}
        key={editingItem ? `edit-${editingItem.id}` : 'add-new'}
      >
        <Form ref={formRef}  
            initialData={editingItem || {}}
            labelWidth={100}>
            <Form.FormItem label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入名称" disabled={!!editingItem} />
          </Form.FormItem>
          <Form.FormItem label="分组" name="dictGroup" rules={[{ required: true, message: '请输入分组' }]}>
            <Input placeholder="请输入分组" disabled={!!editingItem} />
          </Form.FormItem>
          <Form.FormItem label="字典Key" name="dictKey" rules={[{ required: true, message: '请输入字典Key' }]}>
            <Input placeholder="请输入字典Key" disabled={!!editingItem} />
          </Form.FormItem>
          <Form.FormItem label="字典标签" name="dictLabel">
            <Input placeholder="请输入字典标签" disabled={!!editingItem} />
          </Form.FormItem>
           <Form.FormItem label="字典类型" name="valueType" rules={[{ required: true, message: '请选择类型' }]}>
                <Select disabled={!!editingItem}
                  options={typeOptions} 
                  placeholder="请选择字典类型" 
                  onChange={(val) => {
                    setCurrentValueType(val as string);
                    formRef.current?.setFieldsValue({ dictValue: '', dictImage: '' });
                  }}
                />
            </Form.FormItem>
          {currentValueType === 'JSON' ? (
            <>
              <Form.FormItem label="字典值" name="dictValue" rules={[{ required: true, message: '请输入字典值' }]}>
                <Textarea placeholder="请输入JSON内容" rows={4} />
              </Form.FormItem>
              <Form.FormItem label="图片" name="dictImage">
                <CoverImageUploader simple />
              </Form.FormItem>
            </>
          ) : (
            <Form.FormItem label="字典值" name="dictValue" rules={[{ required: true, message: '请输入字典值' }]}>
              <Input placeholder="请输入字典值" />
            </Form.FormItem>
          )}
          <Form.FormItem label="排序" name="sort">
            <Input type="number" disabled={!!editingItem} placeholder="请输入排序" />
          </Form.FormItem>
          {/* <Form.FormItem label="状态" name="status">
            <Switch defaultChecked />
          </Form.FormItem> */}
          <Form.FormItem label="备注" name="remark">
            <Textarea placeholder="请输入备注" />
          </Form.FormItem>
        </Form>
      </Dialog>
    </div>
  );
}